import { GraphQLClient } from 'graphql-request'

/**
 * The GraphQL endpoint, always absolute.
 *
 * Same-origin by default: Caddy serves the SPA and proxies /graphql from one
 * host in production, and Vite's dev server proxies it in development, so
 * there is no CORS anywhere. But graphql-request v7 parses its endpoint with
 * `new URL()`, which throws on a bare path — so resolve against the current
 * origin rather than handing it "/graphql".
 */
const endpoint = new URL(
  import.meta.env.VITE_GRAPHQL_HTTP_URL ?? '/graphql',
  window.location.origin,
).toString()

export const gqlClient = new GraphQLClient(endpoint, {
  // The session cookie is SameSite=Lax and httpOnly; the browser attaches it.
  credentials: 'include',
  headers: {
    // Half of the CSRF story: mutations require a custom header, which a
    // cross-site form post cannot set. The cookie is the other half.
    'x-nook-client': 'web',
  },
})

/**
 * The closed error code set, mirrored from the Rust side.
 *
 * The server promises these five and nothing else, so this union is exhaustive
 * by contract rather than by hope.
 */
export type ErrorCode =
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'RATE_LIMITED'
  | 'VALIDATION'

/**
 * The code behind a failed request, or null if it had none.
 *
 * Null covers two cases that behave the same way for a caller: an internal
 * server error, which deliberately carries no code, and a network failure that
 * never reached the server at all. Both mean "we cannot say why", so both get
 * the generic message.
 */
export function errorCode(error: unknown): ErrorCode | null {
  if (!error || typeof error !== 'object') return null

  // graphql-request throws ClientError, whose `response.errors` is the GraphQL
  // envelope. Read it structurally rather than importing the class, so a
  // rejected fetch does not have to be a different code path.
  const response = (error as { response?: { errors?: unknown } }).response
  const errors = response?.errors
  if (!Array.isArray(errors) || errors.length === 0) return null

  const code = (errors[0] as { extensions?: { code?: unknown } })?.extensions?.code
  return typeof code === 'string' ? (code as ErrorCode) : null
}

/** The message the server sent, or a fallback the user can act on. */
export function errorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (!error || typeof error !== 'object') return fallback

  const response = (error as { response?: { errors?: unknown } }).response
  const errors = response?.errors
  if (Array.isArray(errors) && errors.length > 0) {
    const message = (errors[0] as { message?: unknown })?.message
    if (typeof message === 'string' && message.length > 0) return message
  }

  // A failed fetch has a message too, but it is "Failed to fetch" — true, and
  // useless to read.
  return fallback
}
